import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, ExternalLink, CheckCircle2, Zap } from "lucide-react";
import aiveliqLogo from "@/assets/aiveliq360-logo.png";
import { SEOHead } from "@/components/seo/SEOHead";

const integrations = [
  {
    name: "HubSpot",
    category: "CRM",
    description: "Sync contacts, deals, and activities bidirectionally with HubSpot CRM.",
    features: ["Contact sync", "Deal pipeline sync", "Activity logging", "Custom field mapping"],
    docsUrl: "https://developers.hubspot.com/docs/api/overview",
    icon: "🟠",
  },
  {
    name: "Salesforce",
    category: "CRM",
    description: "Enterprise-grade integration with Salesforce Sales Cloud and Service Cloud.",
    features: ["Lead & opportunity sync", "Custom object support", "Workflow triggers", "Report integration"],
    docsUrl: "https://developer.salesforce.com/docs",
    icon: "☁️",
  },
  {
    name: "Zoho",
    category: "CRM",
    description: "Connect with Zoho CRM for seamless lead and deal management.",
    features: ["Contact sync", "Deal tracking", "Email integration", "Task automation"],
    docsUrl: "https://www.zoho.com/crm/developer/docs/api/v2/",
    icon: "🔴",
  },
  {
    name: "Pipedrive",
    category: "CRM",
    description: "Integrate with Pipedrive for visual pipeline management and tracking.",
    features: ["Pipeline sync", "Activity tracking", "Deal automation", "Custom fields"],
    docsUrl: "https://developers.pipedrive.com/docs/api/v1",
    icon: "🟢",
  },
  {
    name: "Freshsales",
    category: "CRM",
    description: "Sync leads and contacts with Freshsales for unified customer data.",
    features: ["Lead scoring sync", "Contact management", "Deal stages", "Email tracking"],
    docsUrl: "https://developers.freshworks.com/crm/api/",
    icon: "🟡",
  },
  {
    name: "LinkedIn",
    category: "Social",
    description: "Enrich leads with LinkedIn profile data and automate social outreach.",
    features: ["Profile enrichment", "Company data", "Social selling", "Lead discovery"],
    docsUrl: "https://docs.microsoft.com/en-us/linkedin/",
    icon: "🔵",
  },
  {
    name: "Gmail",
    category: "Email",
    description: "Send and track emails directly from Aiveliq360 using your Gmail account.",
    features: ["Email sending", "Thread tracking", "Template support", "Open tracking"],
    docsUrl: "https://developers.google.com/gmail/api",
    icon: "📧",
  },
  {
    name: "Outlook",
    category: "Email",
    description: "Microsoft 365 integration for email and calendar sync.",
    features: ["Email integration", "Calendar sync", "Meeting scheduling", "Contact sync"],
    docsUrl: "https://docs.microsoft.com/en-us/outlook/",
    icon: "📬",
  },
  {
    name: "Slack",
    category: "Communication",
    description: "Get real-time notifications and updates in your Slack workspace.",
    features: ["Deal alerts", "Lead notifications", "Team mentions", "Custom workflows"],
    docsUrl: "https://api.slack.com/docs",
    icon: "💬",
  },
  {
    name: "WhatsApp",
    category: "Communication",
    description: "Engage prospects via WhatsApp Business Cloud API integration.",
    features: ["Message templates", "Two-way messaging", "Media support", "Delivery tracking"],
    docsUrl: "https://developers.facebook.com/docs/whatsapp",
    icon: "💚",
  },
];

const categories = ["All", "CRM", "Email", "Social", "Communication"];

export default function Integrations() {
  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title="Integrations | Aiveliq360 - Connect Your Sales Tech Stack"
        description="Aiveliq360 integrates seamlessly with HubSpot, Salesforce, LinkedIn, Gmail, Slack, WhatsApp and more. Connect your entire sales tech stack."
      />

      {/* Header */}
      <header className="border-b border-border">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <nav className="flex items-center justify-between">
            <Link to="/">
              <img src={aiveliqLogo} alt="Aiveliq360" className="h-10 object-contain" />
            </Link>
            <div className="flex items-center gap-3">
              <Link to="/pricing">
                <Button variant="ghost" size="sm">Pricing</Button>
              </Link>
              <Link to="/products/ai-sales-os">
                <Button variant="ghost" size="sm">Products</Button>
              </Link>
              <Link to="/auth">
                <Button size="sm" className="gap-1.5">
                  Get Started
                  <ArrowRight className="h-3.5 w-3.5" />
                </Button>
              </Link>
            </div>
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section className="py-16 bg-gradient-to-b from-primary/5 to-transparent">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-primary/10 border border-primary/20 mb-6">
            <Zap className="h-3.5 w-3.5 text-primary" />
            <span className="text-xs font-medium text-primary">Native Integrations</span>
          </div>
          <h1 className="text-3xl lg:text-4xl font-bold mb-4">
            Connect Your Entire{" "}
            <span className="gradient-text">Sales Tech Stack</span>
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8">
            Aiveliq360 integrates out of the box with leading CRMs, communication tools, 
            and productivity apps to create a unified sales operating system.
          </p>
          <div className="flex items-center justify-center gap-4">
            <Link to="/auth">
              <Button size="lg" className="gap-2 ai-glow">
                Start Free Trial
                <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Integration Grid */}
      <section className="max-w-6xl mx-auto px-4 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {integrations.map((integration) => (
            <article
              key={integration.name}
              className="glass rounded-xl p-6 hover:border-primary/30 transition-all duration-300 group"
            >
              <div className="flex items-start gap-4">
                <div className="text-3xl">{integration.icon}</div>
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-lg font-semibold">{integration.name}</h3>
                    <span className="text-xs px-2 py-1 rounded-full bg-muted text-muted-foreground">
                      {integration.category}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground mb-4">
                    {integration.description}
                  </p>
                  <ul className="grid grid-cols-2 gap-2 mb-4">
                    {integration.features.map((feature) => (
                      <li key={feature} className="flex items-center gap-2 text-xs">
                        <CheckCircle2 className="h-3 w-3 text-primary shrink-0" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <a
                    href={integration.docsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-xs text-primary hover:underline"
                  >
                    View API Documentation
                    <ExternalLink className="h-3 w-3" />
                  </a>
                </div>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* Setup Guide */}
      <section className="bg-muted/30 py-16">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-2xl lg:text-3xl font-bold mb-3">
              Quick Setup Guide
            </h2>
            <p className="text-muted-foreground max-w-xl mx-auto">
              Get connected in minutes with our step-by-step integration process.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="glass rounded-lg p-6 text-center">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                <span className="text-lg font-bold text-primary">1</span>
              </div>
              <h3 className="font-semibold mb-2">Navigate to Settings</h3>
              <p className="text-sm text-muted-foreground">
                Go to Settings → Integrations in your Aiveliq360 dashboard.
              </p>
            </div>
            <div className="glass rounded-lg p-6 text-center">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                <span className="text-lg font-bold text-primary">2</span>
              </div>
              <h3 className="font-semibold mb-2">Connect Your App</h3>
              <p className="text-sm text-muted-foreground">
                Click "Connect" and authenticate with your credentials via OAuth.
              </p>
            </div>
            <div className="glass rounded-lg p-6 text-center">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                <span className="text-lg font-bold text-primary">3</span>
              </div>
              <h3 className="font-semibold mb-2">Configure Sync</h3>
              <p className="text-sm text-muted-foreground">
                Choose what data to sync and set up field mappings.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-6xl mx-auto px-4 py-16">
        <div className="glass rounded-xl p-10 text-center gradient-border">
          <h2 className="text-2xl lg:text-3xl font-bold mb-3">
            Need a Custom Integration?
          </h2>
          <p className="text-muted-foreground max-w-lg mx-auto mb-8">
            Our team can help you build custom integrations for your specific tech stack.
            Contact us to discuss your requirements.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/auth">
              <Button size="lg" className="gap-2 ai-glow">
                Get Started
                <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
            <Button size="lg" variant="outline">
              Contact Sales
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border py-8">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <Link to="/">
              <img src={aiveliqLogo} alt="Aiveliq360" className="h-6 object-contain" />
            </Link>
            <nav className="flex flex-wrap items-center justify-center gap-4 text-xs text-muted-foreground">
              <Link to="/pricing" className="hover:text-foreground transition-colors">Pricing</Link>
              <Link to="/products/ai-sales-os" className="hover:text-foreground transition-colors">Products</Link>
              <Link to="/privacy" className="hover:text-foreground transition-colors">Privacy</Link>
              <Link to="/terms" className="hover:text-foreground transition-colors">Terms</Link>
            </nav>
            <p className="text-xs text-muted-foreground">© 2025 Aiveliq360. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
